import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import NlpEngineHeader from './NlpEngineHeader';
import NlpChatView from './NlpChatView';
import GroupingRcaView from './GroupingRcaView';
import { contextSearchQueryURL } from '../../const';
import { addAppLog, sendAppLog, getLogMetaFromPath } from '../../utils/logger';
import { getStoredUser } from '../../utils/authSession';
import './nlp-engine.css';

const INITIAL_MESSAGE =
  "Hello! I'm your Analytics and NLP assistant. Ask me about trends, patterns, and insights from your incident data.";

function getBotReply(data) {
  if (typeof data?.result === 'string' && data.result.trim()) return data.result.trim();
  if (typeof data?.response === 'string' && data.response.trim()) return data.response.trim();
  if (typeof data?.payload === 'string' && data.payload.trim()) return data.payload.trim();
  if (typeof data?.answer === 'string' && data.answer.trim()) return data.answer.trim();
  return 'No results found.';
}

function getErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return err?.message || 'Something went wrong. Please try again.';
}

function logApiStatus(success, apiLabel, err) {
  const { moduleName } = getLogMetaFromPath(window.location.pathname);
  if (success) {
    sendAppLog({
      pathname: window.location.pathname,
      logType: 'S',
      content: `${moduleName} — ${apiLabel} API success`,
    });
    return;
  }
  sendAppLog({
    pathname: window.location.pathname,
    logType: 'E',
    content: `${moduleName} — ${apiLabel} API failed: ${getErrorMessage(err)}`,
  });
}

function AnalyticsNlpEngine() {
  const [searchParams] = useSearchParams();
  const activeView = searchParams.get('view') === 'grouping' ? 'grouping' : 'chat';

  const [connected, setConnected] = useState(false);
  const [dataSource, setDataSource] = useState('Postgres (GCP)');
  const [llmProvider, setLlmProvider] = useState('OpenAI');
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([{ type: 'bot', text: INITIAL_MESSAGE }]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState('Thinking…');
  const [sessionId, setSessionId] = useState('');
  const inputRef = useRef(null);

  const handleNewChat = useCallback(() => {
    setMessages([{ type: 'bot', text: INITIAL_MESSAGE }]);
    setMessage('');
    setSessionId('');
  }, []);

  const handleClearSession = useCallback(() => {
    setSessionId('');
    setMessages([{ type: 'bot', text: INITIAL_MESSAGE }]);
    setMessage('');
  }, []);

  const sendMessage = async (e) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || isLoading) return;

    setMessages((prev) => [...prev, { type: 'user', text: trimmed }]);
    setMessage('');
    setLoadingLabel('Thinking…');
    setIsLoading(true);

    try {
      const user = getStoredUser();
      const formData = new FormData();
      formData.append('query', trimmed);
      formData.append('email', user?.email);
      if (sessionId) {
        formData.append('session_id', sessionId);
      }
      const { data } = await axios.post(contextSearchQueryURL, formData);

      logApiStatus(true, 'query');
      setConnected(true);
      await addAppLog('Analytics and NLP', 'Query', user?.email, 'S', `User query input received ${trimmed}`);

      if (data?.session_id) {
        setSessionId(String(data.session_id));
      }

      await addAppLog('Analytics and NLP', 'Query', user?.email, 'S', 'User query processed successfully');
      setMessages((prev) => [...prev, { type: 'bot', text: getBotReply(data) }]);
    } catch (err) {
      logApiStatus(false, 'query', err);
      setConnected(false);
      await addAppLog('Analytics and NLP', 'Query', getStoredUser()?.email, 'E', `User query processing failed ${err}`);
      setMessages((prev) => [
        ...prev,
        { type: 'bot', text: `Error: ${getErrorMessage(err)}`, isError: true },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  return (
    <div className="nlp-engine-page">
      <NlpEngineHeader
        connected={connected}
        dataSource={dataSource}
        onDataSourceChange={setDataSource}
        llmProvider={llmProvider}
        onLlmProviderChange={setLlmProvider}
        onClearSession={handleClearSession}
        onNewChat={handleNewChat}
      />

      <main className="nlp-engine-body">
        {activeView === 'chat' ? (
          <NlpChatView
            messages={messages}
            isLoading={isLoading}
            loadingLabel={loadingLabel}
            message={message}
            onMessageChange={(e) => setMessage(e.target.value)}
            onSend={sendMessage}
            onKeyDown={handleKeyDown}
            inputRef={inputRef}
          />
        ) : (
          <GroupingRcaView
            dataSource={dataSource}
            onConnectionChange={setConnected}
          />
        )}
      </main>
    </div>
  );
}

export default AnalyticsNlpEngine;
